import { UpToDateBuildService } from './../services/up-to-date-build.service';
import { environment } from './../../environments/environment';
import { InvAdjustmentEntryDialog } from './../common/inv-adjustment-entry/inv-adjustment-entry.component';
import { AcumaticaHttpClient } from './acumatica-http';
import { AcumaticaLoadingSnackbarComponent } from './loading-indicator/loading-snackbar-indicator.component';
import { AcumaticaLoadingDialogComponent } from './loading-indicator/loading-dialog-indicator.component';
import { AcumaticaError, NotLoggedInError, TooManyRequestsError } from './acumatica-errors';
import { AcumaticaLoginCredentials, AcumaticaLoginResult as AcumaticaLoginStatus } from './login-credentials';
import { HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AcumaticaLoginComponent } from './acumatica-login/acumatica-login.component';
import { AcumaticaEntity, AcumaticaEntityService, noteEntity as NoteEntity } from './EntityServices/acumatica-entity';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcumaticaLoadingOptions, LoadingIndicator } from './loading-indicator/AcumaticaLoadingOptions';
import { GetAcumaticaFields } from './acumatica-field-decorator';
import { NoteEditorDialog } from '../common/note-editor-dialog/note-editor-dialog.component';
import { AppConfig } from '../app-config.service';
import { LogService } from '../services/logging.service';
import { AdjustmentDetail } from './EntityServices/Adjustment.service';
import { AlertDialog } from '../common/alert-dialog/alert-dialog.component';

export class AcumaticaRequestOptions {
    // configuration of loading feedback indicator
    loader?: AcumaticaLoadingOptions | null | string;
    // indicator that request is for a long-running Action
    isAction?: boolean;
    // prevent returning previously cached value for request
    noCache?: boolean

    // acumatica request $select
    select?: string | string[];
    // acumatica request $filter
    filter?: string;
    // acumatica request $expand
    expand?: string | string[];
    // acumatica request $custom
    custom?: string | string[];
    // acumatica request $skip
    skip?: number;
    // acumatica request $top
    top?: number;
}

export class AcumaticaFileReference {
    __id!: string;
    __filename!: string;
    __href!: string;
}

@Injectable({
    providedIn: 'root'
})
export class AcumaticaService {
    // Acumatica API path initialization
    private acumaticaConfig = AppConfig.settings.acumatica;
    private EntityURL = this.acumaticaConfig.URL + '/entity/';
    private EndpointName = this.acumaticaConfig.EndpointName;
    private EndpointVersion = this.acumaticaConfig.EndpointVersion;
    private Endpoint = this.EntityURL + this.EndpointName + '/' + this.EndpointVersion + '/';
    private Origin = (new URL(this.acumaticaConfig.URL)).origin;
    private options: any = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        }),
        responseType: 'json',
        withCredentials: true,
        observe: 'response'
    };

    private getUser(): any { return JSON.parse(localStorage.getItem('user') || '{}') }
    private setUser(user: any) {
        localStorage.setItem('user', JSON.stringify(user));
        this.buildService.username = this.username;
    }

    public set RepWarehouseID(value: string | null) {
        let user: any = this.getUser();
        if (value == null) delete user.RepWarehouseID;
        else user.RepWarehouseID = value;
        this.setUser(user);
    };
    public get RepWarehouseID(): string | null { return this.getUser().RepWarehouseID || null; }
    public set RepCustomerID(value: string | null) {
        let user: any = this.getUser();
        if (value == null) delete user.RepCustomerID;
        else user.RepCustomerID = value;
        this.setUser(user);
    }
    public get RepCustomerID(): string | null {
        return this.getUser().RepCustomerID || null;
    }

    public get username(): string | null { return this.getUser().Username || null; }
    private set _username(value: string | null) {
        let user: any = this.getUser();
        if (value == null) delete user.username;
        else user.Username = value;
        this.setUser(user);
    }

    constructor(
        public http: AcumaticaHttpClient,
        public injector: Injector,
        private dialog: MatDialog,
        private snackbar: MatSnackBar,
        private logger: LogService,
        private buildService: UpToDateBuildService
    ) {
        this.loginStatusChange.subscribe(loginStatus => {
            if (!loginStatus.loggedin) {
                this._username = null;
            }
        });
        this.buildService.username = this.username;
    }

    get logAPIRequests(): boolean { return localStorage.getItem('log-acumatica-api-requests') == 'yes'; }
    set logAPIRequests(value) {
        if (value) localStorage.setItem('log-acumatica-api-requests', 'yes');
        else localStorage.removeItem('log-acumatica-api-requests');
    }

    logComponentAPIRequestsToBackend: boolean = false;

    get logAPIRequestsToBackend(): boolean { return localStorage.getItem('log-acumatica-api-requests-to-backend') == 'yes'; }
    set logAPIRequestsToBackend(value) {
        if (value) localStorage.setItem('log-acumatica-api-requests-to-backend', 'yes');
        else localStorage.removeItem('log-acumatica-api-requests-to-backend');
    }

    get logAPIRequestsInJSON(): boolean { return localStorage.getItem('log-acumatica-api-requests-json') == 'yes'; }
    set logAPIRequestsInJSON(value) {
        if (value) localStorage.setItem('log-acumatica-api-requests-json', 'yes');
        else localStorage.removeItem('log-acumatica-api-requests-json');
    }

    get cacheAPIResponses(): boolean { return localStorage.getItem('cache-acumatica-api-responses') == 'yes'; }
    set cacheAPIResponses(value) {
        if (value) localStorage.setItem('cache-acumatica-api-responses', 'yes');
        else localStorage.removeItem('cache-acumatica-api-responses');
    }

    get disableAdjustmentRelease(): boolean { return localStorage.getItem('disable-picking-adjustment-release') == 'yes'; }
    set disableAdjustmentRelease(value) {
        if (value) localStorage.setItem('disable-picking-adjustment-release', 'yes');
        else localStorage.removeItem('disable-picking-adjustment-release');
    }

    loginStatusChange = new Subject<AcumaticaLoginStatus>();
    private onDemandLogin = false; // flag set when trying API request but session expired -- prevents extra "Logging In" loader message

    Login(credentials: AcumaticaLoginCredentials): Observable<AcumaticaLoginStatus>;
    Login(name: string, password: string, company?: string, branch?: string, local?: string): Observable<AcumaticaLoginStatus>;
    Login(name: string | AcumaticaLoginCredentials, password?: string, company?: string, branch?: string, local?: string): Observable<AcumaticaLoginStatus> {
        let credentials = (typeof name === 'string')
            ? new AcumaticaLoginCredentials(
                name,
                password || '',
                company || '',
                branch || '',
                local || 'en-US')
            : name;

        this.call('POST', 'auth/login', credentials, {
            loader: this.onDemandLogin ? null : { message: "Logging in" }
        })
            .subscribe({
                next: () => {
                    this._username = credentials.name;
                    this.loginStatusChange.next(new AcumaticaLoginStatus(true));

                    this.put('MobileAppConfig', undefined, { loader: null, expand: 'MobileAppConfigDetails' }).subscribe(config => {
                        if (config.MobileAppConfigDetails.length > 0) {
                            this.RepCustomerID = config.MobileAppConfigDetails[0].SalesRepCustomer?.value;
                            this.RepWarehouseID = config.MobileAppConfigDetails[0].SalesRepWarehouse?.value;

                            if (environment.mode == 'rep') {
                                if (this.RepWarehouseID == null) {
                                    this.dialog.open(AlertDialog, {
                                        data: {
                                            icon: 'report',
                                            message: "A Sales Rep Warehouse ID has not been assigned to your User profile. Contact I.T.",
                                            buttonText: 'OK'
                                        }
                                    });
                                } else if (this.RepCustomerID == null) {
                                    this.dialog.open(AlertDialog, {
                                        data: {
                                            icon: 'report',
                                            message: "A Sales Rep Customer ID has not been assigned to your User profile. Contact I.T.",
                                            buttonText: 'OK'
                                        }
                                    });
                                }
                            }
                        }
                    });
                },
                error: (err: AcumaticaError) => {
                    localStorage.removeItem('user');
                    this.loginStatusChange.next(new AcumaticaLoginStatus(false, true, err.message));
                }
            });

        return this.loginStatusChange;
    }

    Logout(redirectToLoginScreen: boolean = true) {
        this.loginStatusChange.next(new AcumaticaLoginStatus(false, false, null, redirectToLoginScreen));

        this.call('POST', 'auth/logout').subscribe(() => {
            localStorage.removeItem('user');
        });
    }

    // log out the Acumatica server session, but do not indicate to local application logout has happened server-side
    DEV_RemoteLogout() {
        this.call('POST', 'auth/logout').subscribe(() => { });
    }

    /**
     * checks if the current Acumatica user session is still alive
     * @param promptDialogLogin if the user session has expired will dialog prompt for credentials - default true
     * @returns boolean true when session is alive or login has completed, false if session is dead and not prompting to login
     */
    CheckLogInStatus(promptDialogLogin: boolean = true): Observable<boolean> {
        if (this._checkLogInStatus) return this._checkLogInStatus;

        return this._checkLogInStatus = new Observable<boolean>(subscriber => {
            this.http.get(this.acumaticaConfig.URL, {
                responseType: 'text',
                observe: 'response',
                withCredentials: true,
            }).subscribe(response => {
                let isLoggedIn: boolean = response.url!.indexOf("/Login.aspx?") == -1;

                if (!isLoggedIn && promptDialogLogin) {
                    this._username = null;
                    // Open a login dialog
                    const dialogRef = this.dialog.open(AcumaticaLoginComponent, {
                        disableClose: true,
                        data: {
                            errorMessage: "Your Acumatica session has expired"
                        }
                    });

                    setTimeout(() => {
                        dialogRef.componentInstance.LoggedIn.subscribe(() => {
                            this._checkLogInStatus = undefined;
                            subscriber.next(true);
                            subscriber.complete();
                            dialogRef.close();
                        });
                    }, 100);
                } else {
                    this._checkLogInStatus = undefined;
                    subscriber.next(isLoggedIn);
                    subscriber.complete();
                }
            })
        });
    }
    private _checkLogInStatus?: Observable<boolean>;

    private nextRequestTag(): string {
        // type cast this function to add an `idx` property to the function itself
        var f = <{ (): string; idx: number; }>this.nextRequestTag;
        f.idx = (f.idx || 0) + 1;
        if (f.idx == 11) f.idx = 1;
        return ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'][f.idx - 1];
    }

    call(
        method: string,
        resourceName: string,
        body: any = {},
        requestOptions: AcumaticaRequestOptions = {},
        AcumaticaFields?: any
    ): Observable<any> {
        let isAuthRequest = resourceName.startsWith('auth/');
        // Add params object to options (to include query-string values)
        let send_options = Object.assign({ params: {} }, this.options);

        // copy the request_options parameter to not modify the caller's version
        let request_options = Object.assign(new AcumaticaRequestOptions(), requestOptions);

        // Prepend URL with appropriate endpoint
        let request_url = (isAuthRequest ? this.EntityURL : this.Endpoint) + resourceName;

        // convert single strings to string arrays for Acumatica options
        ['select', 'expand', 'custom'].forEach(term => {
            let value = (<any>request_options)[term];
            if (typeof value == 'string') value = value.split(',').map(value => value.trim());
            (<any>request_options)[term] = value;
        });

        // $expand options must be included in $select if $select is specified
        if (request_options.expand &&
            request_options.select &&
            request_options.expand.length > 0 &&
            request_options.select.length > 0) {
            // combine select and expand into select preventing duplicates
            request_options.select = [
                ...new Set([...request_options.select, ...request_options.expand])
            ];
        }

        // DACs that specify custom fields
        //      class DAC { @AcumaticaField({custom:{view:"ViewName", field:"FieldName"}}) InternalField: type; }
        // Add the custom field to the 'custom' collection
        let selectAll = request_options.select === undefined;

        if (AcumaticaFields == undefined && body instanceof AcumaticaEntity) {
            body = AcumaticaEntity.encode(body);
        }

        // iterate AcumaticaField instructions
        if (AcumaticaFields) {
            // DACs that rename Internal/External fields use the external field name
            //      class DAC { @AcumaticaField("ExternalField") InternalField: type; }
            (<string[]>request_options?.select)?.forEach((value, index) => {
                (<string[]>request_options!.select)[index] = AcumaticaFields.rename_internal[value] || value;
            })

            // DACs that specify Custom fields...
            //      class DAC { @AcumaticaField({ custom: {view:"ViewName", field:"UsrFieldName"} }) InternalField: type; }
            for (var property in AcumaticaFields.custom.internal) {
                let customField = AcumaticaFields.custom.internal[property];
                // when getting all fields or the field is in the select[] list
                let selectIndex = (<string[]>request_options?.select)?.indexOf(property);
                if (selectAll || selectIndex > -1) {
                    if (<string[]>request_options!.custom === undefined) request_options!.custom = [];
                    // add the custom View.ExternalField to the $select list
                    (<string[]>request_options!.custom).push(customField.view + '.' + customField.field)
                    // remove the internal name from the $select list
                    if (selectIndex > -1) (<string[]>request_options?.select).splice(selectIndex, 1);
                }
            }
        }

        // Add Acumatica-special terms to query-string object
        ['select', 'filter', 'skip', 'top', 'expand', 'custom'].forEach(term => {
            let value = (<any>request_options)[term];
            if (Array.isArray(value)) value = value.join(',');
            if (value) send_options.params['$' + term] = (value instanceof Array)
                ? value.join(',')
                : value.toString();
        });

        // extract loading indicator options
        let loading_options: AcumaticaLoadingOptions;
        if (request_options.loader === null) {
            loading_options = { indicator: LoadingIndicator.hidden }
        } else if (typeof (request_options.loader) == 'string') {
            loading_options = { message: request_options.loader };
        } else {
            loading_options = Object.assign({}, request_options.loader);
        }
        delete request_options['loader'];

        // log the API request going to acumatica
        let log_body = body instanceof File ? body : Object.assign({}, body);
        if (isAuthRequest && log_body.password) log_body.password = "*******"
        let requestID = this.nextRequestTag();
        if (this.logAPIRequests) {
            let args = [method, request_url, send_options.params, log_body];
            if (this.logAPIRequestsToBackend || this.logComponentAPIRequestsToBackend) {
                try {
                    this.logger.logType(
                        'Acumatica',
                        this.username,
                        args,
                        log_body ? JSON.stringify(log_body) : undefined,
                        this.logComponentAPIRequestsToBackend
                    );
                }
                catch (loggingEx) {
                    console.error(loggingEx);
                }
            }

            args.unshift('🔼' + requestID);

            if (this.logAPIRequestsInJSON) args.push(JSON.stringify(log_body));
            console.log.apply(this, args);
        }

        // invoke the HTTP request and get the response Observable
        let result: Observable<any>;
        switch (method) {
            case 'POST':
                result = this.http.AcumaticaPost(request_url, body, send_options);
                break;
            case 'GET':
                result = this.http.AcumaticaGet(request_url, send_options);
                break;
            case 'PUT':
                result = this.http.AcumaticaPut<any>(request_url, body, send_options);
                break;
            case 'DELETE':
                result = this.http.AcumaticaDelete<any>(request_url, send_options);
                break;
            default:
                throw new Error("Invalid method type to acumatica.call \"" + method + "\"");
        }

        return new Observable(observer => {
            // open the loading indicator -- returns a function that dismisses it
            let dismissLoadingIndicator = this.startLoading(loading_options);

            // create callback function for an error occuring
            let responseErrorHandler = (error: HttpErrorResponse) => {
                dismissLoadingIndicator();

                // timeout to allow GUI update and dismiss loading indicators
                setTimeout(() => {
                    let typedError = AcumaticaError.getTypedError(error);
                    if (typedError instanceof NotLoggedInError) {
                        // User is not logged in -- allow re-login via dialog
                        let dialogRef = this.LoginExpiredSession();

                        // When successfully logged in
                        let acumatica_service = this;
                        let retry_invoked = false;
                        setTimeout(() => {
                            dialogRef.componentInstance.LoggedIn.subscribe(() => {
                                // dialogRef.close();
                                // retry the request
                                if (retry_invoked) return;
                                retry_invoked = true;
                                acumatica_service.call(method, resourceName, body, {
                                    ...{ loader: loading_options },
                                    ...request_options
                                }).subscribe(response => {
                                    observer.next(response)
                                    observer.complete();
                                });
                            });
                        }, 100);
                    } else {
                        observer.error(typedError);
                        observer.complete();
                    }
                });
            };

            // create callback function for a successful response
            let responseSuccessHandler = (response: HttpResponse<any>) => {
                // close the loading indicator
                dismissLoadingIndicator();
                // log the API response
                this.logAPIRequests && console.log('🔽' + requestID, response.body);
                // emit the response to the caller
                observer.next(response.body);
                observer.complete();
            };

            // subscribe (start) the API request
            result.subscribe({
                next: (response: HttpResponse<any>) => {
                    // handle long running async process (Actions)
                    if (request_options.isAction == true)
                        this.action_await(response).subscribe(responseSuccessHandler, responseErrorHandler);
                    else
                        responseSuccessHandler(response);
                },
                error: responseErrorHandler
            });
        });
    }

    private _loginExpiredSessionInProgress?: MatDialogRef<AcumaticaLoginComponent>;
    LoginExpiredSession(): MatDialogRef<AcumaticaLoginComponent> {
        if (this._loginExpiredSessionInProgress) return this._loginExpiredSessionInProgress;

        // User is not logged in -- allow re-login via dialog
        this._username = null;
        this.onDemandLogin = true;
        // Open a login dialog
        let dialogRef = this._loginExpiredSessionInProgress = this.dialog.open(AcumaticaLoginComponent, {
            disableClose: true,
            data: {
                errorMessage: "Your Acumatica session has expired"
            }
        });

        setTimeout(() => {

            dialogRef.componentInstance.LoggedIn.subscribe(() => {
                dialogRef.close();
            });

            dialogRef.afterClosed().subscribe(() => {
                this.onDemandLogin = false;
                this._loginExpiredSessionInProgress = undefined;
            });

        })

        return dialogRef;
    }

    get(entityName: string, key: string | string[], options?: AcumaticaRequestOptions): Observable<any>;
    get(entityService: AcumaticaEntityService, key: string | string[], options?: AcumaticaRequestOptions): Observable<any>;
    get(e: string | AcumaticaEntityService, key: string | string[], options?: AcumaticaRequestOptions): Observable<any> {
        let entityName: string;
        let AcumaticaFields: any = undefined;
        if (e instanceof AcumaticaEntityService) {
            entityName = e.entityName;
            AcumaticaFields = GetAcumaticaFields(e);
        } else {
            entityName = e;
        }
        if (key instanceof Array) key = key.join('/');
        let target = entityName + (key ? '/' + key : '');

        return this.call('GET', target, null, options, AcumaticaFields);
    }

    getEntities(e: string | AcumaticaEntityService, options: AcumaticaRequestOptions) {
        let entityName: string;
        entityName = (e instanceof AcumaticaEntityService)
            ? e.entityName
            : e;

        return this.call('GET', entityName, null, options);
    }

    put(entityName: string, entity?: AcumaticaEntity, options?: AcumaticaRequestOptions): Observable<any> {
        return this.call('PUT', entityName, entity, options);
    }

    delete(entityName: string, key: string | string[], options?: AcumaticaRequestOptions): Observable<void> {
        if (key instanceof Array) key = key.join('/');
        let target = entityName + (key ? '/' + key : '');
        return this.call('DELETE', target, undefined, {
            ...{ loader: { message: "Deleting " + entityName } },
            ...options
        });
    }

    // perform an Acumatica REST API Action
    action(
        entityName: string,
        actionName: string,
        entity: any = {},
        parameters: any = null,
        loader_options: AcumaticaLoadingOptions | null = null
    ) {
        entity = AcumaticaEntity.encode(entity);
        parameters = AcumaticaEntity.encode(parameters || {});
        let actionData = {
            entity: entity,
            parameters: parameters
        };

        return this.call('POST', entityName + '/' + actionName, actionData, { loader: loader_options, isAction: true });
    }

    // Acumatica Actions will return Status=202 and 'location' header while in process
    // Returns Status=204 when complete (though no real response content to speak of)
    // Recursively called function while Status=202 with incrementing delay. Resolves when Status=204
    private action_await(response: HttpResponse<any>, delay: number = 5000): Observable<any> {
        return new Observable<any>(subscriber => {
            if (response.status == 202) {
                // long running operation in process -- wait delay ms then get redirect location
                setTimeout(() => {
                    // create the request
                    let redirect = response.headers.get('location');
                    let request_url = this.Origin + redirect;
                    let ob: Observable<any> = this.http.AcumaticaGet(request_url, this.options);
                    // subscribe to the request
                    ob.subscribe({
                        // when it's done
                        next: (new_response: HttpResponse<any>) => {
                            // send the response back in to recursive call
                            this.action_await(new_response, Math.min(delay + 500, 2000))
                                // send response of the recursive call out through this call's subscriber
                                .subscribe(subscriber);
                        },
                        error: error => {
                            subscriber.error(error);
                        }
                    });
                }, delay);
            } else if (response.status == 204) {
                // long running operation completed successfully
                subscriber.next(response);
            } else {
                console.error('Unexpected action_await result', response);
                throw Error("Unexpected Action result -- see console");
            }
        });
    }

    // private loadingDismissers: (() => void)[] = [];
    public startLoading(options: AcumaticaLoadingOptions): () => void {
        let dismisser: () => void;
        switch (options?.indicator || LoadingIndicator.dialog) {
            case LoadingIndicator.dialog:
                let dialog_ref = this.dialog.open(AcumaticaLoadingDialogComponent, {
                    disableClose: true,
                    data: options
                });
                dismisser = () => { dialog_ref.close(); }
                break;
            case LoadingIndicator.snackbar:
                let snack_ref = this.snackbar.openFromComponent(AcumaticaLoadingSnackbarComponent, {
                    data: options
                });
                dismisser = () => { snack_ref.dismiss(); }
                break;
            case LoadingIndicator.hidden:
                if (options.message) console.log(options.message);
                break;
            default:
                console.error('Loading indicator not imiplemented', options.indicator);
                break;
        }

        return (immediate: boolean = false) => {
            if (!dismisser) return;
            if (!immediate && options.delayClose)
                setTimeout(dismisser, options.delayClose);
            else
                setTimeout(dismisser);
        }
    }

    public putFile(entityName: string, key: string | string[], file: any, filename?: string, options?: AcumaticaRequestOptions): Observable<void> {
        if (key instanceof Array) key = key.join('/');
        if (filename === undefined) filename = file.name;
        let target = entityName + '/' + key + '/files/' + filename;
        return this.call('PUT', target, file, options);
    }

    public editNote(label: string, entity: NoteEntity) {
        let noteDialogRef = this.dialog.open(NoteEditorDialog, {
            data: {
                label: label,
                note: entity.note
            }
        });
        noteDialogRef.afterClosed().subscribe(response => {
            entity.note = (response || '') == ''
                ? null
                : response.trim();
        });
    }

    public editInventoryAdjustment(adjustment: AdjustmentDetail): Promise<AdjustmentDetail | undefined> {
        return new Promise<AdjustmentDetail | undefined>(resolve => {
            let adjDialogRef = this.dialog.open(InvAdjustmentEntryDialog, {
                data: { adjustment: adjustment }
            });
            adjDialogRef.afterClosed().subscribe(response => {
                if (response === '' || adjustment.AdjustQtyTo == null) response = undefined;
                resolve(response);
            });
        });
    }
}